import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, signal, untracked, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranscriptService } from '../services/transcript.service.js';
import { TRANSCRIPT_FLAG_LOW_CONF } from '../models/transcript.model.js';
import { highlightMatchSegments } from '../utils/search-query.js';
import { TooltipDirective } from '../directives/tooltip.directive.js';

// «Речь» → режим «Текст» (admin-only, experimental): full transcript, current line follows
// the playhead, click a line → seek, instant search with Moment Search syntax.
//
// Perf contract: the parent passes `activeLine` (an index that changes every few seconds),
// NEVER playerTime — an input that changes every frame would mark this OnPush view dirty
// 60×/s and re-check thousands of rows. Rows use `content-visibility:auto` so only the
// visible ones are laid out/painted.

interface Row {
  i: number;
  s: number;
  ts: string;
  t: string;
  lowConf: boolean;
  match: boolean;
  segs: Array<{ text: string; isMatch: boolean }> | null;
}

function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

@Component({
  selector: 'app-transcript-panel',
  standalone: true,
  imports: [CommonModule, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'flex flex-col flex-1 min-h-0' },
  template: `
    <div class="p-2 border-b border-gray-800 bg-gray-900/60 space-y-1.5">
      <div class="flex gap-1">
        <input type="text"
               [value]="ts.textInput()"
               (input)="onInput($event)"
               (keydown.enter)="onEnter($event)"
               placeholder="слово, «фраза», -исключить · Enter — следующее"
               appTooltip="Синтаксис как в ПОИСКе: запятая — ИЛИ, пробел — И, кавычки — точная фраза, минус — исключить."
               class="flex-1 min-w-0 bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 focus:border-teal-500 focus:outline-none placeholder:text-gray-600" />
        <button (click)="submit()" [disabled]="!ts.textInput().trim()"
                class="px-3 py-1.5 bg-teal-600 hover:bg-teal-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-bold rounded transition-colors">
          Найти
        </button>
      </div>
      <div class="flex items-center gap-3 text-[11px] text-gray-400 flex-wrap">
        <label class="flex items-center gap-1.5 cursor-pointer hover:text-gray-300 select-none">
          <input type="checkbox" [checked]="ts.textExact()" (change)="toggleExact()" class="h-3 w-3 rounded bg-gray-700 border-gray-600 text-teal-500 focus:ring-0" />
          точное совпадение
        </label>
        @if (ts.textMatches(); as m) {
          <label class="flex items-center gap-1.5 cursor-pointer hover:text-gray-300 select-none">
            <input type="checkbox" [checked]="onlyMatches()" (change)="onlyMatches.set(!onlyMatches())" class="h-3 w-3 rounded bg-gray-700 border-gray-600 text-teal-500 focus:ring-0" />
            только совпадения
          </label>
          @if (m.invalid) {
            <span class="text-red-400">нужно хотя бы одно слово</span>
          } @else {
            <span class="flex items-center gap-1 font-mono text-teal-300">
              <button (click)="step(-1)" [disabled]="m.lines.length === 0" class="px-1 hover:text-white disabled:opacity-40" appTooltip="Предыдущее (Shift+Enter)">‹</button>
              {{ m.lines.length ? (cursor() + 1) + '/' + m.lines.length : '0 совп.' }}
              <button (click)="step(1)" [disabled]="m.lines.length === 0" class="px-1 hover:text-white disabled:opacity-40" appTooltip="Следующее (Enter)">›</button>
            </span>
          }
          <button (click)="clear()" class="hover:text-red-400" appTooltip="Сбросить поиск">✕</button>
        }
        @if (!follow()) {
          <button (click)="resumeFollow()" class="ml-auto px-2 py-0.5 rounded bg-indigo-900/50 hover:bg-indigo-800/60 text-indigo-200 border border-indigo-700/50">
            ↓ К текущей
          </button>
        }
      </div>
    </div>
    <div #scroller class="relative flex-1 min-h-0 overflow-y-auto max-h-[70vh] md:max-h-none py-1 scrollbar-thin scrollbar-thumb-gray-800"
         (wheel)="pauseFollow()" (touchmove)="pauseFollow()">
      @for (row of rows(); track row.i) {
        <div class="tline flex gap-2 px-2 py-0.5 cursor-pointer border-l-2"
             [attr.data-line]="row.i"
             [ngClass]="rowClass(row)"
             (click)="onRowClick(row)">
          <span class="font-mono text-[10px] text-indigo-300/70 shrink-0 pt-0.5 w-12 text-right">{{ row.ts }}</span>
          <span class="text-xs leading-snug break-words min-w-0" [class.opacity-60]="row.lowConf">
            @if (row.segs) {
              @for (seg of row.segs; track $index) {
                @if (seg.isMatch) {
                  <mark class="bg-teal-500/40 text-white rounded px-0.5">{{ seg.text }}</mark>
                } @else {
                  <span>{{ seg.text }}</span>
                }
              }
            } @else {
              {{ row.t }}
            }
          </span>
        </div>
      }
      @if (rows().length === 0) {
        <div class="text-center p-8 text-gray-500 italic text-sm">Совпадений нет</div>
      }
    </div>
  `,
  styles: [`
    .tline {
      content-visibility: auto;
      contain-intrinsic-size: auto 24px;
    }
  `]
})
export class TranscriptPanelComponent implements AfterViewInit, OnDestroy {
  readonly ts = inject(TranscriptService);

  private activeLineSig = signal(-1);
  private rangeSig = signal<{ start: number; end: number } | null>(null);

  /** Line under the playhead (−1 = none). */
  @Input() set activeLine(v: number) { this.activeLineSig.set(v); }
  /** Selected speech segment — its lines get a soft tint. */
  @Input() set range(v: { start: number; end: number } | null) { this.rangeSig.set(v); }
  @Output() seek = new EventEmitter<number>();

  @ViewChild('scroller') private scroller?: ElementRef<HTMLDivElement>;

  readonly follow = signal(true);
  readonly onlyMatches = signal(false);
  readonly cursor = signal(0);
  private focusedLine = signal(-1);
  private rafId = 0;
  private viewReady = signal(false);

  private clocks = computed(() => (this.ts.transcript()?.lines ?? []).map(l => clock(l[0])));

  readonly rows = computed<Row[]>(() => {
    const doc = this.ts.transcript();
    if (!doc) return [];
    const m = this.ts.textMatches();
    const clocks = this.clocks();
    const only = this.onlyMatches() && !!m && !m.invalid;
    const indices = only ? m!.lines : null;
    const n = indices ? indices.length : doc.lines.length;
    const out: Row[] = new Array(n);
    for (let k = 0; k < n; k++) {
      const i = indices ? indices[k] : k;
      const line = doc.lines[i];
      const match = !!m && !m.invalid && m.set.has(i);
      out[k] = {
        i, s: line[0], ts: clocks[i], t: line[2],
        lowConf: ((line[3] ?? 0) & TRANSCRIPT_FLAG_LOW_CONF) !== 0,
        match,
        segs: match ? highlightMatchSegments(line[2], m!.keywords, m!.exact) : null,
      };
    }
    return out;
  });

  constructor() {
    // Follow the playhead: scroll the active line into view (scroller only — never the page).
    effect(() => {
      const line = this.activeLineSig();
      const follow = this.follow();
      if (!this.viewReady() || !follow || line < 0) return;
      untracked(() => this.scheduleScrollTo(line, 'follow'));
    });
  }

  ngAfterViewInit(): void {
    this.viewReady.set(true);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.rafId);
  }

  rowClass(row: Row): string {
    if (row.i === this.activeLineSig()) return 'bg-indigo-900/50 text-white border-l-indigo-400';
    if (row.i === this.focusedLine()) return 'bg-teal-900/40 text-gray-100 border-l-teal-400';
    const r = this.rangeSig();
    if (r && row.s >= r.start && row.s < r.end) return 'bg-gray-800/60 text-gray-200 border-l-gray-500 hover:bg-gray-700/60';
    return (row.match ? 'text-gray-200' : 'text-gray-400') + ' border-l-transparent hover:bg-gray-800/70';
  }

  onRowClick(row: Row): void {
    this.focusedLine.set(-1);
    this.follow.set(true);
    this.seek.emit(row.s);
  }

  onInput(e: Event): void {
    this.ts.textInput.set((e.target as HTMLInputElement).value);
  }

  onEnter(e: Event): void {
    const ke = e as KeyboardEvent;
    const input = this.ts.textInput().trim();
    if (!input) return;
    if (input !== this.ts.textQuery()) this.submit();
    else this.step(ke.shiftKey ? -1 : 1);
  }

  submit(): void {
    this.ts.runTextSearch();
    const m = this.ts.textMatches();
    if (!m || m.invalid || m.lines.length === 0) return;
    // Start from the first match at/after the playhead — that's what «next» means mid-stream.
    const active = Math.max(0, this.activeLineSig());
    const k = m.lines.findIndex(i => i >= active);
    this.jumpTo(k >= 0 ? k : 0);
  }

  step(dir: 1 | -1): void {
    const m = this.ts.textMatches();
    if (!m || m.lines.length === 0) return;
    const n = m.lines.length;
    this.jumpTo(((this.cursor() + dir) % n + n) % n);
  }

  private jumpTo(k: number): void {
    const m = this.ts.textMatches();
    if (!m) return;
    this.cursor.set(k);
    const line = m.lines[k];
    this.focusedLine.set(line);
    this.follow.set(false);
    this.scheduleScrollTo(line, 'jump');
  }

  toggleExact(): void {
    this.ts.textExact.update(v => !v);
    this.cursor.set(0);
  }

  clear(): void {
    this.ts.clearTextSearch();
    this.onlyMatches.set(false);
    this.focusedLine.set(-1);
    this.cursor.set(0);
  }

  pauseFollow(): void {
    if (this.follow()) this.follow.set(false);
  }

  resumeFollow(): void {
    this.focusedLine.set(-1);
    this.follow.set(true);
  }

  private scheduleScrollTo(line: number, mode: 'follow' | 'jump'): void {
    cancelAnimationFrame(this.rafId);
    // Two passes: content-visibility rows above the target may change height once they
    // render, so the first scroll can land a bit off — the second frame corrects it.
    this.rafId = requestAnimationFrame(() => {
      this.scrollTo(line, mode);
      this.rafId = requestAnimationFrame(() => this.scrollTo(line, mode));
    });
  }

  private scrollTo(line: number, mode: 'follow' | 'jump'): void {
    const scroller = this.scroller?.nativeElement;
    if (!scroller) return;
    const el = scroller.querySelector<HTMLElement>(`[data-line="${line}"]`);
    if (!el) return;
    const top = el.offsetTop; // scroller is `relative` → offsetTop is within its content
    const bottom = top + el.offsetHeight;
    const viewTop = scroller.scrollTop;
    const viewBottom = viewTop + scroller.clientHeight;
    // Follow mode only scrolls when the line leaves the comfortable middle band.
    if (mode === 'follow' && top >= viewTop + 24 && bottom <= viewBottom - scroller.clientHeight / 3) return;
    scroller.scrollTop = Math.max(0, top - scroller.clientHeight / 3);
  }
}
